import { redirect } from "next/navigation";
import {
  TLoginResponse,
  TSocialProvider,
  TResponseServerAction,
  TUser,
  ResetResponse,
  TGenericResponse,
  TRole,
} from "./types";
import {
  api,
  ErrorResponseData,
  isAxiosError,
  type AxiosError,
} from "@/lib/axios";
import {
  deleteCookieByName,
  getCookieByName,
  getSessionApiKeySync,
  setCookieByName,
} from "@/lib/cookies";
import { getCountryFromLocale } from "@/lib/country";
import { revalidatePath } from "next/cache";
import { broadcastAuthSessionChanged } from "@/lib/authSession";

/** Same source as `ky` requests: cookie first, then localStorage (SPA). */
export const getToken = async () => {
  return getSessionApiKeySync();
};

export const getApiKeyToken = getToken;

export const loginWithEmail = async (
  payload: {
    email: string;
    password: string;
    role: TRole;
    deviceToken?: string;
  },
  redirectPath?: string,
): Promise<TResponseServerAction<TLoginResponse>> => {
  try {
    const response = await api.post<TGenericResponse<TLoginResponse>>(
      "/accounts/login/email",
      {
        email: payload.email,
        password: payload.password,
        role: payload.role,
        deviceToken: payload.deviceToken || "",
      },
    );

    console.log({ responseLogin: response.data?.data });

    await setCookieByName("x-api-key", response.data.data.apiKey || "");
    await setCookieByName("role", response.data.data.role || "");
    await setCookieByName(
      "id",
      response?.data?.data?.id != null ? String(response.data.data.id) : "",
    );

    if (typeof window === "undefined") {
      throw redirect(redirectPath || "/");
    }

    return {
      status: "success",
      message: response.data.data.message || "Login successful",
      metaData: response.data.data,
      redirectPath: redirectPath || undefined,
    };
  } catch (error) {
    if (
      !isAxiosError(error) &&
      error instanceof Error &&
      error.message.includes("NEXT_REDIRECT")
    ) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    const axiosError = error as AxiosError<ErrorResponseData>;

    console.log({ errorLogin: axiosError });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Login failed"
          : "Login failed",
      errorType:
        axiosError.response?.status === 499 ? "account-not-verified" : "",
    };
  }
};

export const requestOtpLogin = async (payload: {
  email: string;
  role: TRole;
  deviceToken?: string;
}): Promise<TResponseServerAction<{ userId: string }>> => {
  try {
    const response = await api.post<TGenericResponse<{ userId: string }>>(
      "/accounts/login/otp",
      {
        email: payload.email,
        role: payload.role,
        deviceToken: payload.deviceToken || "",
      },
    );

    return {
      status: "success",
      message: "OTP sent successfully",
      metaData: {
        userId: response.data.data.userId,
      },
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Failed to send OTP"
          : "Failed to send OTP",
      errorType:
        axiosError.response?.status === 499 ? "account-not-verified" : "",
    };
  }
};

export const verifyOtpLogin = async (
  payload: {
    userId: string;
    otpCode: string;
  },
  redirectPath?: string,
): Promise<TResponseServerAction<TLoginResponse>> => {
  try {
    const response = await api.post<TGenericResponse<TLoginResponse>>(
      "/accounts/login/otp/verify",
      {
        userId: payload.userId,
        otpCode: payload.otpCode,
      },
    );

    await setCookieByName("x-api-key", response.data.data.apiKey || "");
    await setCookieByName("role", response.data.data.role || "");
    await setCookieByName(
      "id",
      response?.data?.data?.id != null ? String(response.data.data.id) : "",
    );

    throw redirect(redirectPath || "/");
  } catch (error) {
    if (
      !isAxiosError(error) &&
      error instanceof Error &&
      error.message.includes("NEXT_REDIRECT")
    ) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Login failed"
          : "Login failed",
      errorType:
        axiosError.response?.status === 499 ? "account-not-verified" : "",
    };
  }
};

export const loginWithPhoneNumber = async (
  payload: {
    phone: string;
    password: string;
    role: TRole;
    deviceToken?: string;
  },
  redirectPath?: string,
): Promise<TResponseServerAction<TLoginResponse>> => {
  try {
    const res = await api.post<TGenericResponse<TLoginResponse>>(
      "/accounts/login/phone",
      {
        phone: payload.phone,
        password: payload.password,
        role: payload.role,
        deviceToken: payload.deviceToken || "",
      },
    );

    await setCookieByName("x-api-key", res.data.data.apiKey || "");
    await setCookieByName("role", res.data.data.role || "");
    await setCookieByName("id", res?.data?.data?.id?.toString() || "");

    // For server-side rendering or when we need immediate redirect
    if (typeof window === "undefined") {
      throw redirect(redirectPath || "/");
    }

    // For client components, return success with the data
    return {
      status: "success",
      message: res.data.data.message,
      metaData: res.data.data,
      redirectPath: redirectPath || undefined,
    };
  } catch (error) {
    if (
      !isAxiosError(error) &&
      error instanceof Error &&
      error.message.includes("NEXT_REDIRECT")
    ) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    const axiosError = error as AxiosError<ErrorResponseData>;
    // console.log({ error: axiosError.response });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Login failed"
          : "Login failed",
      errorType:
        axiosError.response?.status === 499 ? "account-not-verified" : "",
    };
  }
};

export const loginWithSocialProvider = async (
  payload: {
    provider: TSocialProvider;
    access_token: string;
    role: TRole;
    deviceToken?: string;
  },
  redirectPath?: string,
  isClient: boolean = false,
): Promise<TResponseServerAction<TLoginResponse>> => {
  try {
    console.log({
      idToken: payload.access_token,
      role: payload.role || "Personal",
      deviceToken: payload.deviceToken || "",
      countryCode: getCountryFromLocale(),
    });
    const response = await api.post<TGenericResponse<TLoginResponse>>(
      `/accounts/login/firebase`,
      {
        idToken: payload.access_token,
        role: payload.role || "Personal",
        deviceToken: payload.deviceToken || "",
        countryCode: getCountryFromLocale(),
      },
    );

    const root = (response.data ?? {}) as unknown as Record<string, unknown>;
    const nested = root.data as Record<string, unknown> | undefined;
    const loginPayload = (
      nested && typeof nested === "object" && "apiKey" in nested
        ? nested
        : root
    ) as TLoginResponse & { message?: string };
    const apiKey = loginPayload?.apiKey?.trim() || "";
    const roleVal = loginPayload?.role;
    const idVal = loginPayload?.id;

    if (!apiKey) {
      return {
        status: "error",
        message: "Invalid login response: missing session token",
      };
    }

    await setCookieByName("x-api-key", apiKey);
    await setCookieByName("role", roleVal || "");
    await setCookieByName("id", idVal != null ? String(idVal) : "");

    // For server-side rendering or when we need immediate redirect
    if (typeof window === "undefined" && !isClient) {
      throw redirect(redirectPath || "/");
    }

    if (isClient) {
      revalidatePath("/");
    }

    // For client components, return success with the data
    return {
      status: "success",
      message: loginPayload?.message || "Login successful",
      metaData: loginPayload,
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes("NEXT_REDIRECT")) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ error: axiosError.response?.data });

    return {
      status: "error",
      message: error instanceof Error ? error.message : "Social login failed",
    };
  }
};

export const requestOtpForgotPasswordByEmail = async (payload: {
  email: string;
  role: TRole;
}): Promise<TResponseServerAction<{ userId: string }>> => {
  try {
    const response = await api.post<TGenericResponse<{ userId: string }>>(
      "/accounts/forgot-password/email",
      payload,
    );

    return {
      status: "success",
      message: "OTP sent successfully",
      metaData: {
        userId: response.data.data.userId,
      },
    };
  } catch (error) {
    console.error("Send OTP error:", error);
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Failed to send OTP",
    };
  }
};

export const requestOtpForgotPasswordByPhone = async (payload: {
  phone: string;
  role: TRole;
}): Promise<TResponseServerAction<{ userId: string }>> => {
  try {
    const response = await api.post<TGenericResponse<{ userId: string }>>(
      "/accounts/forgot-password/phone",
      payload,
    );

    return {
      status: "success",
      message: "OTP sent successfully",
      metaData: {
        userId: response.data.data.userId,
      },
    };
  } catch (error) {
    console.error("Send OTP error:", error);
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Failed to send OTP",
    };
  }
};

export const registerWithEmail = async (
  payload: {
    fullName: string;
    email: string;
    password: string;
    role: string;
    languageCode: string;
    countryShortName: string;
  },
  isRedirect: boolean,
): Promise<TResponseServerAction<TUser>> => {
  try {
    const res = await api.post<TGenericResponse<TUser>>(
      "/accounts/register/email",
      payload,
    );

    // For server-side rendering or when we need immediate redirect
    if (typeof window === "undefined" && isRedirect) {
      throw redirect(
        `/auth/otp?contact=${encodeURIComponent(payload.email)}&user_id=${
          res.data.data.id
        }&type=email&from=register`,
      );
    }

    // For client components, return success with the data
    return {
      status: "success",
      message: "Registration successful",
      metaData: res.data.data,
    };
  } catch (error) {
    console.log({ error });

    if (error instanceof Error && error.message.includes("NEXT_REDIRECT")) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    const axiosError = error as AxiosError;

    return {
      status: "error",
      message: error instanceof Error ? error.message : "Registration failed",
      errorType:
        axiosError.response?.status === 499 ? "account-not-verified" : "",
    };
  }
};

export const registerWithPhone = async (
  payload: {
    fullName: string;
    phone: string;
    password: string;
    role: string;
    languageCode: string;
    countryShortName: string;
  },
  // redirectPath?: string
): Promise<TResponseServerAction<TUser>> => {
  try {
    const res = await api.post<TGenericResponse<TUser>>(
      "/accounts/register/phone",
      payload,
    );

    // await setCookieByName("token", res?.data?.data?.token || "");

    // For server-side rendering or when we need immediate redirect
    if (typeof window === "undefined") {
      throw redirect(
        `/auth/otp?contact=${encodeURIComponent(payload.phone)}&user_id=${
          res.data.data.id
        }&type=phone&from=register`,
      );
    }

    // For client components, return success with the data
    return {
      status: "success",
      message: "Registration successful",
      metaData: res.data.data,
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes("NEXT_REDIRECT")) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    const axiosError = error as AxiosError;

    return {
      status: "error",
      message: error instanceof Error ? error.message : "Registration failed",
      errorType:
        axiosError.response?.status === 499 ? "account-not-verified" : "",
    };
  }
};

export const resendVerificationByEmail = async (payload: {
  email: string;
  role: TRole;
}): Promise<TResponseServerAction<{ userId: string }>> => {
  try {
    const response = await api.post<TGenericResponse<{ userId: string }>>(
      `/accounts/resend-verification/email`,
      payload,
    );

    return {
      status: "success",
      message: "Verification email sent successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError.response?.data?.message ||
        (error instanceof Error
          ? error.message
          : "Failed to send verification email"),
    };
  }
};

export const resendVerificationByPhone = async (payload: {
  phone: string;
  role: TRole;
}): Promise<TResponseServerAction<{ userId: string }>> => {
  try {
    const response = await api.post<TGenericResponse<{ userId: string }>>(
      `/accounts/resend-verification/phone`,
      payload,
    );

    return {
      status: "success",
      message: "Verification phone sent successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError.response?.data?.message ||
        (error instanceof Error
          ? error.message
          : "Failed to send verification email"),
    };
  }
};

export const completeVerification = async (payload: {
  userId: string;
  otpCode: string;
}): Promise<
  TResponseServerAction<{
    id: string;
    fullName: string;
    apiKey: string;
    role: string;
    languageId: number;
    language: string;
  }>
> => {
  try {
    const response = await api.post<
      TGenericResponse<{
        id: string;
        fullName: string;
        apiKey: string;
        role: string;
        languageId: number;
        language: string;
      }>
    >(`/accounts/verify`, payload);

    await setCookieByName("x-api-key", response.data.data.apiKey || "");
    await setCookieByName("role", response.data.data.role || "");
    await setCookieByName("id", response?.data?.data?.id?.toString() || "");

    return {
      status: "success",
      message: "OTP verified successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : "Verification failed"),
    };
  }
};

export async function logout(): Promise<TResponseServerAction<null>> {
  "use server";

  try {
    await deleteCookieByName("id");
    await deleteCookieByName("x-api-key");
    await deleteCookieByName("role");

    // For server-side rendering or when we need immediate redirect
    if (typeof window === "undefined") {
      throw redirect("/");
    }

    broadcastAuthSessionChanged();

    // For client components, return success
    return {
      status: "success",
      message: "Logged out successfully",
      metaData: null,
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes("NEXT_REDIRECT")) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    console.error("Logout error:", error);
    return {
      status: "error",
      message: error instanceof Error ? error.message : "Logout failed",
    };
  }
}

// export const verifyResetOtp = async (payload: {
//   userId: string;
//   otpCode: string;
// }): Promise<TResponseServerAction<null>> => {
//   try {
//     await api.post<VerifyResetOtpResponse>("/auth/verify-reset-otp", payload);

//     return {
//       status: "success",
//       message: "OTP verified successfully",
//       metaData: null,
//     };
//   } catch (error) {
//     console.error("Verify OTP error:", error);
//     return {
//       status: "error",
//       message: error instanceof Error ? error.message : "Failed to verify OTP",
//     };
//   }
// };

export const resetPassword = async (
  payload: {
    userId: string;
    password: string;
    otp: string;
  },
  redirectPath?: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.post<ResetResponse>("/accounts/change-password", payload);

    // For server-side rendering or when we need immediate redirect
    if (redirectPath && typeof window === "undefined") {
      throw redirect(redirectPath);
    }

    // For client components, return success
    return {
      status: "success",
      message: "Password reset successfully",
      metaData: null,
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes("NEXT_REDIRECT")) {
      // This is a redirect error from Next.js, let it propagate
      throw error;
    }

    console.error("Reset password error:", error);
    return {
      status: "error",
      message:
        error instanceof Error ? error.message : "Failed to reset password",
    };
  }
};

export const blockAccount = async (
  userId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.post<TGenericResponse>(`/accounts/${userId}/block`);

    revalidatePath("/");

    return {
      status: "success",
      message: "Account blocked successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : "Failed to block account"),
    };
  }
};

export const unblockAccount = async (
  userId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse>(`/accounts/${userId}/block`);

    revalidatePath("/");

    return {
      status: "success",
      message: "Account unblocked successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : "Failed to unblock account"),
    };
  }
};

export const getListOfPersonsBlockedByMe = async (): Promise<
  TResponseServerAction<TUser[]>
> => {
  try {
    const response =
      await api.get<TGenericResponse<TUser[]>>("/accounts/me/block");

    return {
      status: "success",
      message: "Block status retrieved successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : "Failed to get block status"),
    };
  }
};
